import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { useVehiclesList } from '@/features/vehicles/api';
import { useCreateMaintenancePlan } from './api';

const PLAN_TYPES: Array<{ value: 'preventive' | 'corrective'; label: string }> = [
  { value: 'preventive', label: 'Preventiva' },
  { value: 'corrective', label: 'Corretiva' },
];

const ITEM_TYPES: Array<{ value: string; label: string }> = [
  { value: 'service', label: 'Serviço' },
  { value: 'part', label: 'Peça' },
  { value: 'inspection', label: 'Inspeção' },
];

interface ItemDraft {
  key: string;
  description: string;
  type: string;
}

function newItem(): ItemDraft {
  return {
    key: Math.random().toString(36).slice(2, 10),
    description: '',
    type: 'service',
  };
}

interface MaintenancePlanFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MaintenancePlanFormDialog({
  open,
  onOpenChange,
}: MaintenancePlanFormDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [vehicleId, setVehicleId] = useState<string>('all');
  const [type, setType] = useState<'preventive' | 'corrective'>('preventive');
  const [intervalKm, setIntervalKm] = useState('');
  const [intervalDays, setIntervalDays] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([newItem()]);

  const vehiclesQuery = useVehiclesList({ limit: 100, active: 'true', sort: 'plate' });
  const create = useCreateMaintenancePlan();

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setVehicleId('all');
      setType('preventive');
      setIntervalKm('');
      setIntervalDays('');
      setItems([newItem()]);
    }
  }, [open]);

  const updateItem = (key: string, patch: Partial<ItemDraft>) => {
    setItems((arr) => arr.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };
  const removeItem = (key: string) => {
    setItems((arr) => (arr.length === 1 ? arr : arr.filter((it) => it.key !== key)));
  };
  const addItem = () => setItems((arr) => [...arr, newItem()]);

  const validItems = items.filter((i) => i.description.trim().length > 0);
  const hasInterval = intervalKm.trim().length > 0 || intervalDays.trim().length > 0;
  const canSubmit =
    name.trim().length > 0 && validItems.length > 0 && hasInterval && !create.isPending;

  const handleSubmit = async () => {
    try {
      await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        vehicleId: vehicleId === 'all' ? undefined : vehicleId,
        type,
        intervalKm: intervalKm ? Number.parseInt(intervalKm, 10) : undefined,
        intervalDays: intervalDays ? Number.parseInt(intervalDays, 10) : undefined,
        items: validItems.map((it, idx) => ({
          description: it.description.trim(),
          type: it.type,
          sortOrder: idx,
        })),
      });
      toast.success(`Plano "${name.trim()}" criado`);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Novo plano de manutenção</DialogTitle>
          <DialogDescription>
            Defina a periodicidade (por km ou dias). O sistema vai te avisar quando vencer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nome do plano *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Revisão dos 10.000 km"
              maxLength={120}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Descrição</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Opcional"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="vehicle">Veículo</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger id="vehicle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os veículos (plano geral)</SelectItem>
                  {(vehiclesQuery.data?.data ?? []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.plate}
                      {(v.brand || v.model) && ` — ${[v.brand, v.model].filter(Boolean).join(' ')}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="type">Tipo *</Label>
              <Select
                value={type}
                onValueChange={(v) => setType(v as 'preventive' | 'corrective')}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="intervalKm">A cada quantos km</Label>
              <Input
                id="intervalKm"
                inputMode="numeric"
                value={intervalKm}
                onChange={(e) => setIntervalKm(e.target.value.replace(/\D/g, ''))}
                placeholder="Ex.: 10000"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="intervalDays">A cada quantos dias</Label>
              <Input
                id="intervalDays"
                inputMode="numeric"
                value={intervalDays}
                onChange={(e) => setIntervalDays(e.target.value.replace(/\D/g, ''))}
                placeholder="Ex.: 180"
              />
            </div>
          </div>
          {!hasInterval && (
            <p className="text-xs text-destructive">
              Defina pelo menos um intervalo (km ou dias).
            </p>
          )}

          {/* Itens */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Itens do plano *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="size-4" />
                Adicionar item
              </Button>
            </div>

            <div className="space-y-2">
              {items.map((item, idx) => (
                <div
                  key={item.key}
                  className="flex items-start gap-2 rounded-lg border border-border bg-card p-3"
                >
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground">
                    {idx + 1}
                  </div>
                  <Input
                    className="flex-1"
                    value={item.description}
                    onChange={(e) => updateItem(item.key, { description: e.target.value })}
                    placeholder="Ex.: Troca de óleo do motor"
                    maxLength={255}
                  />
                  <Select
                    value={item.type}
                    onValueChange={(v) => updateItem(item.key, { type: v })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ITEM_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive"
                      onClick={() => removeItem(item.key)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {validItems.length === 0 && (
              <p className="text-xs text-destructive">
                Pelo menos um item com descrição é obrigatório.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending ? 'Criando…' : 'Criar plano'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
